import {useContext, useState} from 'react'
import {useNavigate} from 'react-router-dom'

import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import AskContacts from './AskContacts'
import AwaitingContacts from './AwaitingContacts'
import PasswordStep from './PasswordStep'
import PreparingRecovery from './PreparingRecovery'

import {asyncActions} from '../../../lib/asyncUtil'
import {connect} from '../../../state/hooks/tools'
import {clearSessionTokens} from '../../../lib/auth'
import {createResource, deleteResource, readEndpoint} from '../../../lib/asyncMethods'
import {DispatchContext} from '../../../state/contexts'
import {
  REQUESTED,
  awaitsKeyPair,
  canAskContacts,
  canCompleteRecovery,
  isKeyPairUnavailable,
  isWrongPassword,
  needsKeyPairRetry,
  readyContacts
} from '../../../lib/recovery'
import {RECOVERY_CONTACTS, RECOVERY_REQUEST, SESSION, SIGN_OUT} from '../../../state/actionTypes'

const REQUESTS = 'recovery-requests'
const KEY_PAIR = 'recovery-requests/key-pair'
const COMPLETE = 'recovery-requests/complete'

/**
 * A recovery in progress, which is one record and five screens.
 *
 * The record's status decides everything here — the API allows exactly one action from each, and
 * refuses the rest — so every branch below is read from it rather than from what a failed request
 * came back with. That matters most where the two disagree: a status code says a key pair is still
 * being generated, but only a fresh read says whether it has since arrived.
 */
const RecoveryRequest = props => {
  const [error, setError] = useState('')
  const dispatchs = useContext(DispatchContext)
  const navigate = useNavigate()
  const {recovery} = props
  const holders = readyContacts({invites: props.invites, pendingWraps: props.pendingWraps})

  const refresh = () => props.readRecovery(REQUESTS)

  // A refused password is the one failure worth naming: the generic message cannot say which of the
  // two passwords in the user's head was wanted, and the status code is the only thing that knows.
  const failed = response => {
    setError(
      isWrongPassword(response) ?
        "That isn't the password you use to sign in now. Check it and try again." :
        response.message
    )
  }

  const askContacts = () => {
    setError('')

    props.askContacts({endpoint: REQUESTS}).then(response => {
      // a pair that was not ready a moment ago may be ready now, and either way the record is the
      // authority on which screen belongs here
      if (response.success || isKeyPairUnavailable(response))
        return refresh()

      return failed(response)
    })
  }

  const retryKeyPair = ({password}) => {
    setError('')

    props.retryKeyPair({endpoint: KEY_PAIR, attributes: {password}}).then(response => {
      if (response.success)
        return refresh()

      return failed(response)
    })
  }

  // The session was issued while the key was still out of reach, and the API's own answer asks for a
  // fresh sign-in, so the recovery ends by clearing everything this one accumulated.
  const completeRecovery = ({password}) => {
    setError('')

    props.completeRecovery({endpoint: COMPLETE, attributes: {password}}).then(response => {
      if (!response.success)
        return failed(response)

      return props.signOut('auth/sign-out').then(() => {
        clearSessionTokens()
        Object.values(dispatchs).forEach(dispatch => dispatch(asyncActions(SIGN_OUT).success({data: {}})))
        navigate('/auth/sign-in')
      })
    })
  }

  if (awaitsKeyPair(recovery))
    return <PreparingRecovery onRefresh={refresh} />

  if (needsKeyPairRetry(recovery)) {
    return (
      <PasswordStep
        heading="We couldn't finish setting up"
        action='Try again'
        error={error}
        loading={props.loading}
        handleSubmit={retryKeyPair}>
        <Typography variant='body1' gutterBottom>
          Something went wrong while preparing your recovery, and it needs starting again. Nothing
          has been lost — your contacts still hold their pieces.
        </Typography>
      </PasswordStep>
    )
  }

  if (canCompleteRecovery(recovery)) {
    return (
      <PasswordStep
        heading="You're ready to finish"
        action='Finish recovery'
        error={error}
        loading={props.loading}
        handleSubmit={completeRecovery}>
        <Typography variant='body1' gutterBottom>
          Your contacts sent back enough pieces. Enter your password and we'll put your account back
          together.
        </Typography>
        <Typography className='caption' variant='body2' gutterBottom>
          You'll be signed out and asked to sign in once more.
        </Typography>
      </PasswordStep>
    )
  }

  if (canAskContacts(recovery)) {
    if (recovery.status === REQUESTED) {
      return (
        <AwaitingContacts
          holders={holders}
          onAsk={askContacts}
          onRefresh={refresh}
          error={error}
          loading={props.loading}
        />
      )
    }

    return <AskContacts holders={holders} onAsk={askContacts} error={error} loading={props.loading} />
  }

  return null
}

RecoveryRequest.propTypes = {
  recovery: PropTypes.object.isRequired,
  invites: PropTypes.array,
  pendingWraps: PropTypes.array
}

const mapStateToProps = ({recoveryRequest, recoveryContacts}) => ({
  loading: recoveryRequest.loading || recoveryContacts.loading
})

// Asking answers with the contacts it wrote to rather than with the recovery, so it is kept out of
// the slice holding the record — a collection that took both would leave the two indistinguishable.
const actionCreators = ({recoveryRequest, recoveryContacts, auth}) => ({
  readRecovery: readEndpoint(recoveryRequest, RECOVERY_REQUEST),
  askContacts: createResource(recoveryContacts, RECOVERY_CONTACTS),
  retryKeyPair: createResource(recoveryRequest, RECOVERY_REQUEST),
  completeRecovery: createResource(recoveryRequest, RECOVERY_REQUEST),
  signOut: deleteResource(auth, SESSION)
})

export default connect(mapStateToProps, actionCreators)(RecoveryRequest)
